<template>
  <DashboardLayout :breadcrumbs="breadcrumbs">
    <div class="container is-fluid">
      <div class="level mo">
        <div class="level-left">
          <div class="control">
            <h1 class="title is-5">{{ $t('issueTracker.issue') }} №{{ detail.id }}</h1>
          </div>
          <div class="control" v-if="detail.state === 'new' && detail.is_author">
            <a class="edit" @click="edit">
              <b-icon icon="pencil" size="is-small" class="icon"></b-icon>
              {{ $t('interface.edit') }}
            </a>
          </div>
          <div class="control" v-if="!detail.is_classified">
            <a :href="detail.file" target="_blank" class="button is-small ml-20">
              <b-icon icon="download" size="is-small"></b-icon>
              <span>{{ $t('issueTracker.downloadFile') }}</span>
            </a>
          </div>
          <div class="control" v-if="(detail.state === 'new' && !detail.is_author)
                                      || (detail.state === 'in_process' && detail.is_executor)">
            <button class="button is-success is-small ml-20" @click="nextState">
              <span v-if="detail.state === 'new'">{{ $t('interface.startExecution') }}</span>
              <span v-if="detail.state === 'in_process'">{{ $t('interface.endExecution') }}</span>
            </button>
          </div>
          <div class="control" v-if="!['cancelled', 'done'].includes(detail.state) && detail.is_author">
            <button class="button is-danger is-small ml-20" @click="cancel">{{ $t('issueTracker.reject') }}</button>
          </div>
        </div>
      </div>
      <div class="columns">
        <div class="column is-7">
          <div class="document description">
            <table>
              <tr>
                <td><strong>{{ $t('issueTracker.author') }}</strong></td>
                <td>{{ detail.author__initials }}</td>
                <td><strong>{{ $t('issueTracker.executor') }}</strong></td>
                <td v-if="detail.executor__initials">{{ detail.executor__initials }}</td>
                <td v-else>&ndash;</td>
              </tr>
              <tr>
                <td><strong>{{ $t('issueTracker.priority') }}</strong></td>
                <td>
                  <b-tag :type="`is-${priorityColors[detail.priority]}`">
                    {{ $t(`workflow.priorities.${detail.priority}`) }}
                  </b-tag>
                </td>
                <td><strong>{{ $t('issueTracker.currentState') }}</strong></td>
                <td>
                  <b-tag :type="`is-${stateColors[detail.state]}`">
                    {{ $t(`workflow.states.${detail.state}`) }}
                  </b-tag>
                </td>
              </tr>
              <tr>
                <td><strong>{{ $t('issueTracker.category') }}</strong></td>
                <td>{{ detail.category__name }}</td>
                <td><strong>{{ $t('issueTracker.term') }}</strong></td>
                <td v-if="detail.term">
                  {{ detail.term | timeFormat }}
                  {{ detail.term | localedDateWithYear }}
                </td>
                <td v-else>&ndash;</td>
              </tr>
            </table>
            <div>
              <strong>{{ $t('interface.description') }}</strong>
              <p>{{ detail.description }}</p>
            </div>
          </div>
          <Comments :comments="detail.comments"
                    class="mt-20"
                    @create="createComment"
                    @edit="editComment"
                    @remove="removeComment">
          </Comments>
        </div>
        <div class="column is-5">
          <h3 class="title is-6">{{ $t('issueTracker.progress') }}</h3>
          <Timeline class="mt-15"
                    :data="timeline">
          </Timeline>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<script>
  import {mapState} from 'vuex'
  import DashboardLayout from '@/components/DashboardLayout'
  import DocumentInformation from '@/components/DocumentInformation'
  import Tasks from '@/components/Tasks'
  import Comments from '@/components/Comments'
  import Timeline from '@/components/Timeline'
  import {timelineItem} from '@/constants'
  import {priorityColors, stateColors} from '@/colorTypes'

  export default {
    components: {
      DashboardLayout,
      DocumentInformation,
      Tasks,
      Timeline,
      Comments
    },
    data() {
      return {
        priorityColors, stateColors
      }
    },
    methods: {
      edit() {
        this.$router.push({
          name: 'editIssueTracker',
          params: {issueId: this.detail.id}
        })
      },
      nextState() {
        this.$store.dispatch('IssueTracker/nextState', null).then(() => this.loadIssue())
      },
      cancel() {
        this.$dialog.confirm({
          title: this.$t('issueTracker.reject'),
          message: this.$t('issueTracker.rejectAcceptation'),
          confirmText: this.$t('issueTracker.reject'),
          cancelText: this.$t('interface.cancel'),
          type: 'is-danger',
          hasIcon: true,
          onConfirm: () =>
            this.$store.dispatch('IssueTracker/cancel', null).then(() => this.$router.push({name: 'issues'}))
        })
      },
      loadIssue() {
        this.$store.dispatch('IssueTracker/getDetail', this.$route.params.issueId)
      },
      createComment(text) {
        this.$store.dispatch('IssueTracker/createComment', text)
      },
      editComment(comment) {
        this.$store.dispatch('IssueTracker/updateComment', comment)
      },
      removeComment(id) {
        this.$store.dispatch('IssueTracker/removeComment', id)
      }
    },
    mounted() {
      this.loadIssue()
    },
    computed: {
      ...mapState('IssueTracker', ['detail']),
      ...mapState('Account', ['account']),
      timeline() {
        return this.detail.states.map(state => {
          return {
            ...timelineItem,
            date: state.created_at,
            title: this.$t(`workflow.states.${state.state}`),
            note: `${state.author ? state.author.initials : ''}${state.note ? ` – ${state.note}` : ''}`,
          }
        })
      },
      canChangeState() {
        return true
      },
      breadcrumbs() {
        return [
          {name: 'issues', path: 'issues'},
          {name: 'issueDetail'}
        ]
      }
    }
  }
</script>

<style lang="scss" scoped>
  .edit {
    font-size: 13px !important;
    margin-left: 10px;

    .icon {
      margin-right: 2px;
    }
  }

  .dialog {
    .input {
      resize: vertical;
      min-height: 80px;
    }
  }
</style>
